import { Form, useFormContext } from "@/components/form";
import { Stack, Button } from "@mui/material";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import { notifyError } from "@/shared/notification";
import { FormFieldWithError } from "@/components/form/form-field-with-error";
import { MonedaOperacionField } from "@/features/cajas/caja-actual/components/moneda-operacion-field";

interface Pago {
    tipoPago: string;
    monto: number;
    tipoTarjeta?: string;
    procesadorPago?: string;
    numeroCheque?: string;
    bancoEmisor?: string;
    otroTipoTarjeta?: string;
}

const TIPOS_PAGO = [
    { value: "efectivo", label: "Efectivo" },
    { value: "tarjeta_credito", label: "Tarjeta de Crédito" },
    { value: "tarjeta_debito", label: "Tarjeta de Débito" },
    { value: "cheque", label: "Cheque" },
    { value: "transferencia", label: "Transferencia" }
];

function PagoRow({
    index,
    pagos,
    onEliminar
}: {
    index: number;
    pagos: Pago[];
    onEliminar: (index: number) => void;
}) {
    const form = useFormContext();

    const getTiposDisponibles = (currentTipo: string) => {
        const tiposUsados = pagos.map(p => p.tipoPago).filter(t => t !== currentTipo);
        return TIPOS_PAGO.filter(t => !tiposUsados.includes(t.value));
    };

    return (
        <form.Field name={`pagos.${index}.tipoPago`}>
            {(tipoPagoField) => {
                const tipoPago = tipoPagoField.state.value || "efectivo";
                const esTarjeta = tipoPago === "tarjeta_credito" || tipoPago === "tarjeta_debito";
                const esCheque = tipoPago === "cheque";

                return (
                    <Stack direction="row" gap={1} alignItems="flex-start" flexWrap="wrap">
                        <Form.Select
                            name={`pagos.${index}.tipoPago`}
                            label="Tipo de Pago *"
                            size="small"
                            sx={{ width: '180px' }}
                        >
                            {getTiposDisponibles(tipoPago).map(tipo => (
                                <Form.Select.Option key={tipo.value} value={tipo.value}>
                                    {tipo.label}
                                </Form.Select.Option>
                            ))}
                        </Form.Select>

                        <FormFieldWithError
                            name={`pagos.${index}.monto`}
                            label="Monto *"
                            size="small"
                            sx={{ width: '180px' }}
                            fieldKind="moneda"
                            heightError="60px"
                        />

                        {esTarjeta && (
                            <>
                                <form.Field name={`pagos.${index}.tipoTarjeta`}>
                                    {(tipoTarjetaField) => {
                                        const tipoTarjeta = tipoTarjetaField.state.value;
                                        const esOtraTarjeta = tipoTarjeta === "99";

                                        return (
                                            <>
                                                <form.Field name={`pagos.${index}.tipoTarjeta`}>
                                                    {(field) => {
                                                        const error = field.state.meta?.errors?.[0];

                                                        return (
                                                            <Stack height={error && "70px"}>
                                                                <Form.Select
                                                                    name={`pagos.${index}.tipoTarjeta`}
                                                                    label="Tarjeta"
                                                                    size="small"
                                                                    sx={{ width: '150px' }}

                                                                >
                                                                    <Form.Select.Option value="1">Dinelco</Form.Select.Option>
                                                                    <Form.Select.Option value="2">Visa</Form.Select.Option>
                                                                    <Form.Select.Option value="3">Mastercard</Form.Select.Option>
                                                                    <Form.Select.Option value="4">Amex</Form.Select.Option>
                                                                    <Form.Select.Option value="5">Cabal</Form.Select.Option>
                                                                    <Form.Select.Option value="99">Otro</Form.Select.Option>
                                                                </Form.Select>

                                                                {esOtraTarjeta && (
                                                                    <FormFieldWithError
                                                                        name={`pagos.${index}.otroTipoTarjeta`}
                                                                        label="Denominación Tarjeta *"
                                                                        size="small"
                                                                        placeholder="Ej: Mastercard"
                                                                        slotProps={{
                                                                            htmlInput: {
                                                                                minLength: 4,
                                                                                maxLength: 20
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Stack>
                                                        )
                                                    }
                                                    }
                                                </form.Field>

                                            </>
                                        );
                                    }}
                                </form.Field>

                                <form.Field name={`pagos.${index}.procesadorPago`}>
                                    {(field) => {
                                        const error = field.state.meta?.errors?.[0];

                                        return (
                                            <Stack height={error && "70px"}>
                                                <Form.Select
                                                    name={`pagos.${index}.procesadorPago`}
                                                    label="Procesador"
                                                    size="small"
                                                    sx={{ width: '160px' }}
                                                >
                                                    <Form.Select.Option value="1">Procesado</Form.Select.Option>
                                                    <Form.Select.Option value="2">No Procesado</Form.Select.Option>
                                                </Form.Select>
                                            </Stack>
                                        )
                                    }}
                                </form.Field>
                            </>
                        )}

                        {esCheque && (
                            <>
                                <FormFieldWithError
                                    name={`pagos.${index}.numeroCheque`}
                                    label="Nº Cheque"
                                    size="small"
                                    sx={{ width: '150px' }}
                                    heightError="70px"
                                />

                                <FormFieldWithError
                                    name={`pagos.${index}.bancoEmisor`}
                                    label="Banco"
                                    size="small"
                                    sx={{ width: '150px' }}
                                    heightError="70px"
                                />
                            </>
                        )}

                        <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => onEliminar(index)}
                            sx={{ mt: 1 }}
                        >
                            Eliminar
                        </Button>
                    </Stack>
                );
            }}
        </form.Field>
    );
}

interface DECondicionPagoFieldsProps {
    forAutofactura?: boolean;
}

export default function DECondicionPagoFields({ forAutofactura = false }: DECondicionPagoFieldsProps) {
    const form = useFormContext();

    const handleCondicionChange = (value: string) => {
        if (value === "contado") {
            form.setFieldValue("pagos", [{ tipoPago: "efectivo", monto: 0 }]);
        } else {
            form.setFieldValue("tipoCredito", "plazo");
            form.setFieldValue("plazoDias", "");
        }
    };

    const handleTipoCreditoChange = (value: string) => {
        if (value === "plazo") {
            form.setFieldValue("plazoDias", "");
            form.setFieldValue("numeroCuotas", 1);
        } else {
            form.setFieldValue("numeroCuotas", 1);
            form.setFieldValue("plazoDias", "");
        }
    };

    const monedaOperacionValue = {
        monedaOperacionId: form.getFieldValue("monedaOperacionId"),
        conversionGuarani: form.getFieldValue("conversionGuarani") ?? 1
    };

    const handleMonedaOperacionChange = (val: { monedaOperacionId: string; conversionGuarani: number }) => {
        form.setFieldValue("monedaOperacionId", val.monedaOperacionId);
        form.setFieldValue("conversionGuarani", val.conversionGuarani);
    };

    return (
        <CollapsibleSection title="Condición de Pago">
            <Stack direction="row" gap={2} paddingTop={1} alignItems="center" marginBottom={2}>
                <MonedaOperacionField
                    forAutofactura={forAutofactura}
                    value={monedaOperacionValue}
                    onChange={handleMonedaOperacionChange}
                />
            </Stack>
            <form.Field name="condicionOperacion">
                {(condicionField) => {
                    const condicionOperacion = condicionField.state.value || "contado";

                    return (
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems="flex-start" flexWrap="wrap">
                            <Stack minWidth="200px">
                                <Form.RadioGroup
                                    name="condicionOperacion"
                                    label="Condición de Operación *"
                                    row
                                    onChange={handleCondicionChange}
                                >
                                    <Form.RadioButton value="contado" label="Contado" />
                                    <Form.RadioButton value="credito" label="Crédito" disabled={forAutofactura} />
                                </Form.RadioGroup>
                            </Stack>

                            {condicionOperacion === "contado" && (
                                <form.Field name="pagos" mode="array">
                                    {(field) => {
                                        const pagos: Pago[] = field.state.value || [];

                                        const handleAgregarPago = () => {
                                            const pagoActual: Pago[] = field.state.value || [];

                                            if (pagoActual.length > 0) {
                                                form.resetField(`pagos.${pagoActual.length - 1}.monto`);
                                            }

                                            const tiposUsados = pagoActual.map(p => p.tipoPago);
                                            const tipoDisponible = TIPOS_PAGO.find(t => !tiposUsados.includes(t.value));

                                            if (!tipoDisponible) {
                                                notifyError("Ya se han agregado todos los tipos de pago disponibles");
                                                return;
                                            }

                                            field.pushValue({ tipoPago: tipoDisponible.value, monto: 0 });
                                        };

                                        const handleEliminarPago = (index: number) => {
                                            field.removeValue(index);
                                        };

                                        return (
                                            <Stack gap={1}>
                                                {pagos.map((_, index) => (
                                                    <PagoRow
                                                        key={index}
                                                        index={index}
                                                        pagos={pagos}
                                                        onEliminar={handleEliminarPago}
                                                    />
                                                ))}

                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={handleAgregarPago}
                                                    disabled={pagos.length >= TIPOS_PAGO.length}
                                                    sx={{ alignSelf: 'flex-start' }}
                                                >
                                                    Agregar
                                                </Button>
                                            </Stack>
                                        );
                                    }}
                                </form.Field>
                            )}

                            {condicionOperacion === "credito" && (
                                <form.Field name="tipoCredito">
                                    {(tipoCreditoField) => {
                                        const tipoCredito = tipoCreditoField.state.value || "plazo";

                                        return (
                                            <>
                                                <Stack width={{ xs: '100%', sm: '200px' }} minWidth="180px">
                                                    <Form.RadioGroup
                                                        name="tipoCredito"
                                                        label="Tipo de Crédito"
                                                        row
                                                        onChange={handleTipoCreditoChange}
                                                    >
                                                        <Form.RadioButton value="plazo" label="Plazo" />
                                                        <Form.RadioButton value="cuotas" label="Cuotas" />
                                                    </Form.RadioGroup>
                                                </Stack>

                                                {tipoCredito === "plazo" && (
                                                    <Stack width={{ xs: '100%', sm: '250px' }} minWidth="200px">
                                                        <FormFieldWithError
                                                            name="plazoDias"
                                                            label="Plazo"
                                                            size="small"
                                                            placeholder="Ej: 30 días, 2 meses"
                                                            slotProps={{
                                                                htmlInput: { maxLength: 15, minLength: 2 }
                                                            }}
                                                        />
                                                    </Stack>
                                                )}

                                                {tipoCredito === "cuotas" && (
                                                    <Stack width={{ xs: '100%', sm: '150px' }} minWidth="140px">
                                                        <FormFieldWithError
                                                            name="numeroCuotas"
                                                            label="Nº Cuotas"
                                                            size="small"
                                                            fieldKind="number"
                                                            placeholder="1"
                                                            slotProps={{ htmlInput: { min: 1, maxLength: 3 } }}
                                                        />
                                                    </Stack>
                                                )}
                                            </>
                                        );
                                    }}
                                </form.Field>
                            )}
                        </Stack>
                    );
                }}
            </form.Field>
        </CollapsibleSection>
    );
}

